import { Ear, Eye, Keyboard, Type, type LucideIcon } from 'lucide-react';
import { EnteteEcran } from '../components/ui/Primitives';
import { Icone } from '../components/Icone';
import { OPTIONS_ACCESSIBILITE } from '../accessibilite/options';
import { useAccessibilite } from '../accessibilite/useAccessibilite';
import { useToast } from '../components/ui/useToast';
import { useReferentHandicap } from '../lib/useReferentHandicap';
import type { ReferentHandicap } from '../lib/useParametrage';
import { initialesDuNom } from '../marque/marque';

/**
 * Écran « Accessibilité » (CDC §9), mise en page de la maquette validée
 * (`07-modules-complementaires.js`).
 *
 * Décisions du 26/10/2026 (TF-341) : les boutons ouvrent le panneau
 * d'adaptations, comme dans la maquette ; les cartes ne promettent que ce que
 * la plateforme fait ; la carte FALC laisse place aux commandes du clavier ;
 * le référent handicap est celui que la direction règle dans « Paramétrage ›
 * Accessibilité » — sans référent réglé, sa carte ne s'affiche pas.
 */
export function AccessibilitePage(): JSX.Element {
  const toast = useToast();
  const { etat, basculer, reinitialiser, nombreActives, lectureVocaleDisponible, ouvrirPanneau } =
    useAccessibilite();
  const { data } = useReferentHandicap();
  const referent = data?.referent ?? null;

  return (
    <div className="mx-auto max-w-[1500px] space-y-8 p-4 sm:p-6 lg:p-8">
      <EnteteEcran
        titre="Accessibilité numérique"
        sousTitre="Conformité RGAA 4.1 niveau AA · adaptations dys, sensorielles et motrices"
        actions={
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={ouvrirPanneau}
              aria-haspopup="dialog"
              className="gradient-mint text-codialis-navy rounded-xl px-4 py-2.5 text-sm font-semibold transition"
            >
              Activer les adaptations
            </button>
            {nombreActives > 0 && (
              <button
                type="button"
                onClick={reinitialiser}
                className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-codialis-navy transition hover:bg-gray-50"
              >
                Tout réinitialiser
              </button>
            )}
          </div>
        }
      />

      {/* Bannière RGAA */}
      <div className="grid grid-cols-1 items-center gap-6 rounded-2xl bg-gradient-to-br from-codialis-navy to-codialis-blue p-4 sm:p-6 text-white shadow-card lg:grid-cols-3 lg:p-8">
        <div className="lg:col-span-2 min-w-0">
          <div className="mb-2 text-xs font-bold uppercase tracking-wider text-codialis-mint">
            RGAA 4.1 · niveau AA
          </div>
          <h2 className="font-display mb-2 text-2xl font-bold">
            Une plateforme accessible à tous les apprenants
          </h2>
          <p className="max-w-xl text-sm leading-relaxed text-white/70">
            Modes activables en 1 clic par l'apprenant, compatibilité lecteurs d'écran, navigation
            100% clavier, sous-titres et transcriptions.
          </p>
        </div>
        <div className="text-center">
          <div className="font-display text-5xl font-extrabold text-codialis-mint">AA</div>
          <div className="mt-1 text-xs text-white/60">niveau de conformité visé</div>
        </div>
      </div>

      {/* Carte des réglages interactifs */}
      <div className="overflow-hidden rounded-2xl bg-white shadow-card">
        <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-4 sm:px-6 py-4">
          <h2 className="font-display font-bold text-codialis-navy">
            Adaptations de lecture et de navigation
          </h2>
          <span className="text-xs text-codialis-gray">
            {nombreActives === 0
              ? 'Aucune adaptation active'
              : `${nombreActives} adaptation${nombreActives > 1 ? 's' : ''} active${
                  nombreActives > 1 ? 's' : ''
                }`}
          </span>
        </div>
        <div className="p-4 sm:p-6 space-y-6">
          <p className="text-sm text-codialis-gray">
            Ces réglages sont conservés dans votre navigateur, sur ce poste. Ils ne sont pas
            enregistrés dans votre dossier ni visibles par les autres utilisateurs.
          </p>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {OPTIONS_ACCESSIBILITE.map((option) => {
              const indisponible = option.cle === 'tts' && !lectureVocaleDisponible;
              const actif = etat[option.cle] === true;
              return (
                <button
                  key={option.cle}
                  type="button"
                  role="switch"
                  aria-checked={actif}
                  disabled={indisponible}
                  onClick={() => {
                    basculer(option.cle);
                    toast(
                      `Adaptation ${option.libelle} ${!actif ? 'activée' : 'désactivée'}`,
                    );
                  }}
                  className={`flex flex-col justify-between rounded-2xl border p-4 sm:p-5 text-left transition disabled:cursor-not-allowed disabled:opacity-50 ${
                    actif
                      ? 'border-codialis-mint bg-codialis-mint/10'
                      : 'border-gray-200 bg-white hover:bg-gray-50'
                  }`}
                >
                  <div>
                    <div className="flex items-start justify-between">
                      <Icone nom={option.icone} className="h-6 w-6 text-codialis-sky" />
                      {actif && (
                        <span className="badge-mint rounded px-2 py-0.5 text-[10px] font-bold">
                          actif
                        </span>
                      )}
                    </div>
                    <div className="font-display mt-4 text-sm font-bold text-codialis-navy">
                      {option.libelle}
                    </div>
                    <p className="mt-1.5 text-xs text-codialis-gray leading-relaxed">
                      {indisponible ? 'Non disponible dans ce navigateur' : option.detail}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* 4 cartes des profils de besoins : ce que la plateforme fait vraiment. */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <CarteBesoin
          icone={Type}
          titre="Troubles dys"
          items={[
            'Police adaptée',
            'Espacement et interligne renforcés',
            'Règle de lecture',
            'Lecture vocale de la sélection',
          ]}
        />
        <CarteBesoin
          icone={Eye}
          titre="Malvoyants"
          items={[
            'Lecteurs d’écran (NVDA, VoiceOver)',
            'Contraste renforcé',
            'Jaune sur noir',
            'Texte agrandi jusqu’à +35 %',
          ]}
        />
        <CarteBesoin
          icone={Ear}
          titre="Sourds & malentendants"
          items={['Sous-titres des leçons vidéo', 'Transcription navigable']}
        />
        <CarteBesoin
          icone={Keyboard}
          titre="Mobilité réduite"
          items={[
            'Navigation 100 % clavier',
            'Lien « Aller au contenu »',
            'Focus toujours visible',
            'Échap ferme les fenêtres',
          ]}
        />
      </div>

      {/* 2 cartes du bas */}
      <div className={`grid grid-cols-1 gap-6 ${referent ? 'lg:grid-cols-2' : ''}`}>
        <div className="rounded-2xl bg-white p-4 sm:p-6 shadow-card space-y-4">
          <h2 className="font-display text-lg font-bold text-codialis-navy">Navigation au clavier</h2>
          <ul className="space-y-2.5 text-sm text-codialis-gray">
            <Commande touches={['Tab', 'Maj + Tab']}>passer d’un élément à l’autre</Commande>
            <Commande touches={['Entrée', 'Espace']}>activer un bouton ou une case</Commande>
            <Commande touches={['Échap']}>fermer une fenêtre ou un panneau</Commande>
            <Commande touches={['←', '→']}>changer d’onglet ; Origine et Fin pour le premier et le dernier</Commande>
            <Commande touches={['Maj + flèches']}>sélectionner un texte, lu par la lecture vocale</Commande>
          </ul>
          <p className="text-xs text-codialis-gray">
            Le premier arrêt de tabulation de chaque écran est le lien « Aller au contenu ».
          </p>
          <button
            type="button"
            onClick={ouvrirPanneau}
            aria-haspopup="dialog"
            className="rounded-xl bg-codialis-navy px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-codialis-deep"
          >
            Tester les modes en direct
          </button>
        </div>

        {referent && <CarteReferent referent={referent} />}
      </div>
    </div>
  );
}

function Commande({ touches, children }: { touches: string[]; children: string }): JSX.Element {
  return (
    <li className="grid grid-cols-1 gap-1 sm:grid-cols-[9rem_minmax(0,1fr)] sm:items-baseline sm:gap-3">
      <span className="flex flex-wrap gap-1">
        {touches.map((touche) => (
          <kbd
            key={touche}
            className="rounded-md border border-gray-200 bg-gray-50 px-1.5 py-0.5 font-sans text-xs font-semibold text-codialis-navy"
          >
            {touche}
          </kbd>
        ))}
      </span>
      <span>{children}</span>
    </li>
  );
}

function CarteReferent({ referent }: { referent: ReferentHandicap }): JSX.Element {
  return (
    <div className="rounded-2xl bg-white p-4 sm:p-6 shadow-card space-y-4">
      <h2 className="font-display text-lg font-bold text-codialis-navy">Référent handicap</h2>
      <p className="text-sm text-codialis-gray leading-relaxed">
        Votre interlocuteur pour toute demande d’aménagement de votre parcours, dès l’inscription.
      </p>
      <div className="flex items-center gap-3 rounded-xl bg-gray-50 p-4">
        <div
          aria-hidden="true"
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-codialis-sky to-codialis-blue text-sm font-bold text-white"
        >
          {initialesDuNom(referent.nom)}
        </div>
        <div className="min-w-0">
          <div className="text-sm font-semibold text-codialis-navy">{referent.nom}</div>
          <div className="flex flex-wrap gap-x-2 text-xs text-codialis-gray">
            {referent.email && (
              <a href={`mailto:${referent.email}`} className="break-all text-codialis-blue hover:underline">
                {referent.email}
              </a>
            )}
            {referent.email && referent.telephone && <span aria-hidden="true">·</span>}
            {referent.telephone && (
              <a href={`tel:${referent.telephone.replace(/[^\d+]/g, '')}`} className="text-codialis-blue hover:underline">
                {referent.telephone}
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function CarteBesoin({
  icone: IconeBesoin,
  titre,
  items,
}: {
  icone: LucideIcon;
  titre: string;
  items: string[];
}): JSX.Element {
  return (
    <div className="rounded-2xl bg-white p-4 sm:p-6 shadow-card space-y-3">
      <IconeBesoin aria-hidden="true" className="h-8 w-8 text-codialis-navy" strokeWidth={1.75} />
      <h2 className="font-display text-base font-bold text-codialis-navy">{titre}</h2>
      <ul className="space-y-2 pt-1">
        {items.map((item) => (
          <li key={item} className="flex items-start gap-2.5 text-xs text-codialis-gray">
            <span
              aria-hidden="true"
              className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-codialis-mint"
            />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
